import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { DOCUMENT_STYLE_LIST } from '../../../Utils/DocumentVersion/documentStyleVersions';

/**
 * Visual picker for high-level document style versions.
 * `lockedStyleId` (a saved version is being edited) disables every other layout.
 */
const DocumentStylePicker = ({ t, direction, selectedStyleId, onSelect, versionCounts = {}, lockedStyleId = null }) => {
    const lockedHint = t('VersionLayoutLockedHint')
        || 'הפריסה של גרסה שמורה קבועה. אפשר לערוך אותה או לשכפל אותה; לפריסה אחרת צרו גרסה חדשה מהעיצוב הבסיסי.';
    return (
        <Box dir={direction} sx={{ width: '100%' }}>
            <Typography
                variant="subtitle2"
                sx={{ color: '#5f6368', fontSize: '13px', mb: 1.5, lineHeight: 1.4 }}
                dir={direction}
            >
                {lockedStyleId
                    ? lockedHint
                    : (t('DocumentStyleVersionHint') || 'בחר עיצוב כללי למסמך. ניתן לכוונן צבעים וגדלים בהמשך.')}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: 1.25,
                }}
                dir={direction}
            >
                {DOCUMENT_STYLE_LIST.map((style) => {
                    const selected = selectedStyleId === style.id;
                    const disabled = Boolean(lockedStyleId) && style.id !== lockedStyleId;
                    const select = () => { if (!disabled) onSelect(style.id); };
                    return (
                        <Tooltip
                            key={style.id}
                            title={disabled ? lockedHint : (t(style.descriptionKey) || style.descriptionFallback)}
                            arrow
                            placement="top"
                        >
                            <Box
                                onClick={select}
                                role="button"
                                tabIndex={disabled ? -1 : 0}
                                aria-disabled={disabled}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') select();
                                }}
                                sx={{
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    opacity: disabled ? 0.45 : 1,
                                    filter: disabled ? 'grayscale(0.6)' : 'none',
                                    borderRadius: '14px',
                                    border: selected
                                        ? `2px solid ${style.previewAccent}`
                                        : '2px solid #e8eaed',
                                    background: '#fff',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    transition: 'all 0.2s ease',
                                    boxShadow: selected
                                        ? `0 4px 14px ${style.previewAccent}33`
                                        : '0 1px 4px rgba(0,0,0,0.06)',
                                    '&:hover': disabled ? {} : {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 6px 16px ${style.previewAccent}28`,
                                        borderColor: style.previewAccent,
                                    },
                                }}
                                dir={direction}
                            >
                                {/* Mini document mock */}
                                <Box
                                    sx={{
                                        height: 72,
                                        background: `linear-gradient(160deg, ${style.previewAccent}18 0%, #fff 55%)`,
                                        p: 1,
                                        position: 'relative',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: 10,
                                            borderRadius: '3px',
                                            background: style.previewAccent,
                                            width: style.layout === 'modernBanner' || style.layout === 'navyCard' ? '100%' : '55%',
                                            mb: 0.6,
                                            ml: style.layout === 'modernBanner' || style.layout === 'navyCard' ? 0 : 'auto',
                                            borderRadius: style.layout === 'navyCard' ? '0 0 0 8px' : '3px',
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                                        <Box
                                            sx={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: style.layout === 'softWaves' ? '50%' : '4px',
                                                background: style.previewSecondary || style.previewAccent,
                                                opacity: 0.85,
                                            }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                            <Box
                                                sx={{
                                                    height: 5,
                                                    borderRadius: 1,
                                                    background: '#cfd4da',
                                                    mb: 0.35,
                                                    width: '90%',
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    height: 4,
                                                    borderRadius: 1,
                                                    background: '#e4e7eb',
                                                    width: '70%',
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            height: 6,
                                            borderRadius: 1,
                                            background: style.theme?.tableHeaderBg || style.previewAccent,
                                            opacity: style.theme?.tableHeaderBg === 'transparent' ? 0.35 : 1,
                                            border:
                                                style.theme?.tableHeaderBg === 'transparent'
                                                    ? `1px solid ${style.previewAccent}`
                                                    : 'none',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            mt: 0.4,
                                            height: 4,
                                            borderRadius: 1,
                                            background: '#eee',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            mt: 0.3,
                                            height: 4,
                                            borderRadius: 1,
                                            background: '#f3f3f3',
                                            width: '85%',
                                        }}
                                    />
                                    {selected && (
                                        <CheckCircleIcon
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                ...(direction === 'rtl' ? { left: 4 } : { right: 4 }),
                                                fontSize: 18,
                                                color: style.previewAccent,
                                                background: '#fff',
                                                borderRadius: '50%',
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box sx={{ px: 1, py: 0.85, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            color: '#202124',
                                            lineHeight: 1.25,
                                        }}
                                        dir={direction}
                                    >
                                        {t(style.nameKey) || style.nameFallback}
                                    </Typography>
                                    {versionCounts[style.id] > 0 && (
                                        <Box
                                            component="span"
                                            title={t('SavedDesignVersions') || 'גרסאות שמורות'}
                                            sx={{
                                                flexShrink: 0,
                                                minWidth: 18,
                                                height: 18,
                                                px: 0.5,
                                                borderRadius: '9px',
                                                background: `${style.previewAccent}1f`,
                                                color: style.previewAccent,
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {versionCounts[style.id]}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        </Box>
    );
};

export default DocumentStylePicker;
